import type { TaskDto, TaskPriority } from '../../api/tasks';

/**
 * task-filter (2026-08-18 시안) — 툴바(보기 전환 · 필터 · 업무 검색)의 순수 로직.
 *
 * 화면 컴포넌트와 분리한 이유: 칸반/목록 두 뷰가 **같은 결과 집합**을 봐야 하고,
 * 날짜 경계(기한 초과/오늘/이번 주) 판정은 단위 test 로 고정하는 편이 안전하기 때문이다.
 * 서버 무변경 — 이미 받아 둔 tasks 배열 위에서만 거른다.
 */

export type TaskViewMode = 'kanban' | 'list';
export type TaskFilterScope = 'all' | 'mine';
export type TaskFilterDue = 'all' | 'overdue' | 'today' | 'week';

export interface TaskFilterState {
  /** 담당 범위 — 전체 / 내 것만(로그인 사용자가 담당자에 포함). */
  scope: TaskFilterScope;
  /** 우선순위 — 'ALL' 이면 미적용. */
  priority: TaskPriority | 'ALL';
  /** 마감 — dueDate 없는 태스크는 'all' 이 아닌 한 제외된다. */
  due: TaskFilterDue;
  /** 업무 검색어 — 제목·설명·태그 부분일치(대소문자 무시). */
  query: string;
}

export const EMPTY_TASK_FILTER: TaskFilterState = {
  scope: 'all', priority: 'ALL', due: 'all', query: '',
};

/** 필터 버튼 배지에 찍을 적용 개수 — 검색어는 별도 입력이라 세지 않는다. */
export function activeFilterCount(f: TaskFilterState): number {
  let n = 0;
  if (f.scope !== 'all') n += 1;
  if (f.priority !== 'ALL') n += 1;
  if (f.due !== 'all') n += 1;
  return n;
}

export function isTaskFilterEmpty(f: TaskFilterState): boolean {
  return activeFilterCount(f) === 0 && f.query.trim() === '';
}

/** 로컬 자정 기준 일수 차 (due - today). 파싱 불가면 NaN. */
function dayDiff(dueIso: string, now: number): number {
  const d = new Date(dueIso);
  if (Number.isNaN(d.getTime())) return NaN;
  const t = new Date(now);
  const a = Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  const b = Date.UTC(t.getFullYear(), t.getMonth(), t.getDate());
  return Math.round((a - b) / 86_400_000);
}

function matchDue(task: TaskDto, due: TaskFilterDue, now: number): boolean {
  if (due === 'all') return true;
  if (!task.dueDate) return false;
  const diff = dayDiff(task.dueDate, now);
  if (Number.isNaN(diff)) return false;
  if (due === 'overdue') return diff < 0;
  if (due === 'today') return diff === 0;
  return diff >= 0 && diff <= 6; // week — 오늘 포함 7일
}

function matchQuery(task: TaskDto, q: string): boolean {
  if (!q) return true;
  const hay = [
    task.title,
    task.description ?? '',
    ...(Array.isArray(task.tags) ? task.tags : []),
  ].join(' ').toLowerCase();
  return hay.includes(q);
}

/**
 * 필터 + 검색 적용. `myId` 가 빈 문자열이면 'mine' 범위는 결과가 비게 된다
 * (로그인 정보를 못 읽은 상태에서 남의 태스크를 내 것으로 보여주지 않기 위함).
 */
export function filterTasks(
  tasks: TaskDto[],
  f: TaskFilterState,
  myId: string,
  now: number = Date.now(),
): TaskDto[] {
  if (!Array.isArray(tasks)) return [];
  if (isTaskFilterEmpty(f)) return tasks;
  const q = f.query.trim().toLowerCase();
  return tasks.filter((t) => {
    if (f.scope === 'mine') {
      const al = Array.isArray(t.assignees) ? t.assignees : [];
      if (!myId || !al.includes(myId)) return false;
    }
    if (f.priority !== 'ALL' && t.priority !== f.priority) return false;
    if (!matchDue(t, f.due, now)) return false;
    return matchQuery(t, q);
  });
}
